/**
 * Badges earned from lifetime totals. The server keeps the totals (player_stats) and awards badges when
 * a shift pushes a total past a goal; the game only displays them. Shared so both agree on the goals.
 */
export interface PlayerStats {
  shifts: number;
  metres: number;
  perfectStops: number;
  passengersDropped: number;
  closeCalls: number;
  coins: number;
  /** Longest run of shifts without running a red light. */
  bestCleanStreak: number;
  challengePlays: number;
}

export const EMPTY_STATS: PlayerStats = { shifts: 0, metres: 0, perfectStops: 0, passengersDropped: 0, closeCalls: 0, coins: 0, bestCleanStreak: 0, challengePlays: 0 };

export type StatKey = Exclude<keyof PlayerStats, never>;

export interface Badge {
  id: string;
  name: string;
  icon: string;
  blurb: string;
  stat: StatKey;
  /** The total at which it's earned. For metres, the goal is in metres. */
  goal: number;
  /** Higher = rarer; the leaderboard shows the rarest few. */
  rarity: number;
}

const km = (n: number): number => n * 1000;

export const BADGES: readonly Badge[] = [
  { id: 'perfect-25', name: 'Sharp Stopper', icon: '🎯', blurb: '25 perfect stops', stat: 'perfectStops', goal: 25, rarity: 1 },
  { id: 'perfect-100', name: 'Perfect Driver', icon: '🏅', blurb: '100 perfect stops', stat: 'perfectStops', goal: 100, rarity: 3 },
  { id: 'perfect-500', name: 'Stop Master', icon: '👑', blurb: '500 perfect stops', stat: 'perfectStops', goal: 500, rarity: 6 },
  { id: 'km-10', name: 'Town Runner', icon: '🛣️', blurb: 'Drove 10 kilometres', stat: 'metres', goal: km(10), rarity: 1 },
  { id: 'km-100', name: 'Road Warrior', icon: '🚐', blurb: 'Drove 100 kilometres', stat: 'metres', goal: km(100), rarity: 3 },
  { id: 'km-1000', name: 'Across Ghana', icon: '🇬🇭', blurb: 'Drove 1,000 kilometres', stat: 'metres', goal: km(1000), rarity: 6 },
  { id: 'clean-3', name: 'Obeys the Light', icon: '🚦', blurb: 'No red light run for 3 shifts in a row', stat: 'bestCleanStreak', goal: 3, rarity: 2 },
  { id: 'clean-10', name: 'Law Abiding', icon: '👮', blurb: 'No red light run for 10 shifts in a row', stat: 'bestCleanStreak', goal: 10, rarity: 4 },
  { id: 'clean-30', name: 'Police Favourite', icon: '🛡️', blurb: 'No red light run for 30 shifts in a row', stat: 'bestCleanStreak', goal: 30, rarity: 7 },
  { id: 'riders-100', name: 'Mate Approved', icon: '🧑‍🤝‍🧑', blurb: '100 passengers dropped', stat: 'passengersDropped', goal: 100, rarity: 2 },
  { id: 'riders-500', name: 'People\'s Driver', icon: '🚌', blurb: '500 passengers dropped', stat: 'passengersDropped', goal: 500, rarity: 4 },
  { id: 'riders-2500', name: 'Accra Legend', icon: '🌟', blurb: '2,500 passengers dropped', stat: 'passengersDropped', goal: 2500, rarity: 7 },
  { id: 'close-50', name: 'Daredevil', icon: '😎', blurb: '50 close calls', stat: 'closeCalls', goal: 50, rarity: 2 },
  { id: 'close-250', name: 'Nerves of Steel', icon: '⚡', blurb: '250 close calls', stat: 'closeCalls', goal: 250, rarity: 5 },
  { id: 'coins-200', name: 'Coin Collector', icon: '🪙', blurb: '200 coins picked up', stat: 'coins', goal: 200, rarity: 2 },
  { id: 'coins-1000', name: 'Gold Hands', icon: '💰', blurb: '1,000 coins picked up', stat: 'coins', goal: 1000, rarity: 5 },
  { id: 'daily-1', name: 'Daily Driver', icon: '📅', blurb: 'Played the daily challenge', stat: 'challengePlays', goal: 1, rarity: 1 },
  { id: 'daily-7', name: 'Never Misses', icon: '🔥', blurb: 'Played the daily challenge 7 times', stat: 'challengePlays', goal: 7, rarity: 4 },
];

export function badgeById(id: string): Badge | undefined {
  return BADGES.find((b) => b.id === id);
}

/** Every badge the totals have reached. */
export function earnedBadges(stats: PlayerStats): string[] {
  return BADGES.filter((b) => stats[b.stat] >= b.goal).map((b) => b.id);
}

/** The few to show next to a name: rarest first. */
export function showcaseBadges(ids: readonly string[], limit = 3): string[] {
  return ids
    .map((id) => badgeById(id))
    .filter((b): b is Badge => b !== undefined)
    .sort((a, b) => b.rarity - a.rarity || b.goal - a.goal)
    .slice(0, limit)
    .map((b) => b.id);
}

/** Only the top tier of each family counts as "rarest" per kind, so one family doesn't fill the showcase. */
export function showcaseDistinct(ids: readonly string[], limit = 3): string[] {
  const best = new Map<StatKey, Badge>();
  for (const id of ids) {
    const badge = badgeById(id);
    const current = badge && best.get(badge.stat);
    if (badge && (!current || badge.rarity > current.rarity)) best.set(badge.stat, badge);
  }
  return showcaseBadges([...best.values()].map((b) => b.id), limit);
}
